import StageOrientation from './StageOrientation';
import { srOnlyElement } from '../utils/screen-reader/html';
import FocusOutlines from './seatIcons/FocusOutlines';
import { SyosNonSeat } from './api/types';
import type SyosSeat from './SyosSeat';
import type SeatIconState from './seatIcons/SeatIconState';
import type IconRenderer from './seatIcons/IconRenderer';

function groupSeatsBySectionAndRow(seats: SyosSeat[]): [[SyosSeat[]]] {
  const map = new Map<number, Map<string, SyosSeat[]>>();

  seats.forEach((seat) => {
    const { sectionId, row } = seat;

    let rowMap = map.get(sectionId);

    if (!rowMap) {
      rowMap = new Map();
      map.set(sectionId, rowMap);
    }

    let rowSeats = rowMap.get(row);

    if (!rowSeats) {
      rowSeats = [];
      rowMap.set(row, rowSeats);
    }
    rowSeats.push(seat);
  });

  return [...map.values()].map((el) => [...el.values()]);
}

function makeNonSeatHtml(nonSeat: SyosNonSeat) {
  return `<div class="tn-syos-seat-map__non-seat" style="top: ${nonSeat.y}px; left: ${nonSeat.x}px;">${nonSeat.letter}</div>`;
}

interface SyosSeatMapConfig {
  unitSize: number;
  marginSize: number;
  seats: SyosSeat[];
  nonSeats: SyosNonSeat[];
  rowCount: number;
  columnCount: number;
  stageOrientation: string;
  performanceId: number;
  facilityId: number;
  screenId: number;
  screenDescription: string;
  iconRenderer: IconRenderer;
  rootHeadingLevel: number;
}

class SyosSeatMap {
  width: number;

  height: number;

  unitSize: number;

  marginSize: number;

  seats: SyosSeat[];

  nonSeats: SyosNonSeat[];

  rowCount: number;

  columnCount: number;

  stageOrientation: string;

  performanceId: number;

  facilityId: number;

  screenId: number;

  screenDescription: string;

  iconRenderer: IconRenderer;

  rootHeadingLevel: number;

  constructor({
    unitSize,
    marginSize,
    seats,
    nonSeats,
    rowCount,
    columnCount,
    stageOrientation,
    performanceId,
    facilityId,
    screenId,
    screenDescription,
    iconRenderer,
    rootHeadingLevel,
  }: SyosSeatMapConfig) {
    this.width = (unitSize * columnCount) + (marginSize * (columnCount - 1));
    this.height = (unitSize * rowCount) + (marginSize * (rowCount - 1));
    this.unitSize = unitSize;
    this.marginSize = marginSize;
    this.seats = seats;
    this.nonSeats = nonSeats;
    this.rowCount = rowCount;
    this.columnCount = columnCount;
    this.stageOrientation = stageOrientation;
    this.performanceId = performanceId;
    this.facilityId = facilityId;
    this.screenId = screenId;
    this.screenDescription = screenDescription;
    this.iconRenderer = iconRenderer;
    this.rootHeadingLevel = rootHeadingLevel;
  }

  render() {
    const { width, height } = this;
    const seatMapHeading = this.makeSeatMapHeading();
    const seatMapSvg = this.makeSeatMapSvg();
    const nonSeatsHtml = this.makeNonSeatsHtml();
    const stageIndicatorHtml = this.makeStageIndicatorHtml();

    const el = document.createElement('div');
    el.classList.add('tn-syos-seat-map');
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;

    el.innerHTML = seatMapHeading + seatMapSvg + nonSeatsHtml + stageIndicatorHtml;

    return {
      el,
      width: this.width,
      height: this.height,
    };
  }

  private makeSeatMapHeading() {
    const headingTag = `h${this.rootHeadingLevel + 1}`;

    return srOnlyElement(headingTag, `${this.screenDescription} Seat Map`);
  }

  private makeSeatMapSvg() {
    const {
      width,
      height,
      seats,
      performanceId,
      facilityId,
      screenId,
      screenDescription,
    } = this;

    const titleElementId = 'tn-syos-svg-title';

    const map = new Map<string, SeatIconState>();

    this.seats.forEach(({ icon }) => {
      map.set(icon.default.id, icon.default);

      if (icon.selected) {
        map.set(icon.selected.id, icon.selected);
      }
    });

    const symbolMarkup = [...map.values()]
      .map((i) => this.iconRenderer.getSymbolMarkup(i))
      .join('');

    const sections = groupSeatsBySectionAndRow(seats);

    const focusIndicator = `
      <g id="tn-syos-seat-focus-outline">
        <path id="${FocusOutlines.Circle.id}" d="${FocusOutlines.Circle.path}" />
        <path id="${FocusOutlines.BadgedCircle.id}" d="${FocusOutlines.BadgedCircle.path}"/>
        <path id="${FocusOutlines.Square.id}" d="${FocusOutlines.Square.path}"/>
      </g>`;

    const seatsSvg = `
      <g role="list" aria-labelledby="${titleElementId}">
        ${sections.map((section) => this.makeSectionListItem(section)).join('')}
      </g>`;

    return `
      <svg class="tn-syos-seat-map__svg" xmlns='http://www.w3.org/2000/svg' xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${width} ${height}" width="${width}px" height="${height}px"
        data-tn-performance-id="${performanceId}"
        data-tn-facility-id="${facilityId}"
        data-tn-screen-id="${screenId}"
        focusable="false"
        role="presentation"
      >
        <title id="${titleElementId}">${screenDescription} Sections</title>

        ${symbolMarkup}
        ${seatsSvg}
        ${focusIndicator}
      </svg>
    `;
  }

  private makeRowListItem(row: SyosSeat[], sectionHeadingId: string) {
    const seat = row[0];
    const rowLabel = `Row ${seat.row}`;
    const rowHeadingId = `tn-syos-row-list-heading-id-${seat.sectionId}-${seat.row}`;
    const seatsDescribedByAttributeValue = `${sectionHeadingId} ${rowHeadingId}`;

    const headingLevel = this.rootHeadingLevel + 3;

    const heading = srOnlyElement('text', rowLabel, { id: rowHeadingId, role: 'heading', 'aria-level': headingLevel.toString() });

    return `
    <g role="listitem" aria-label="${rowLabel}">
      ${heading}
      <g role="list" aria-label="${rowLabel}">
        ${row.map((s) => this.makeSeatElement(s, seatsDescribedByAttributeValue)).join('')}
      </g>
    </g>`;
  }

  private makeSectionListItem(section: [SyosSeat[]]) {
    const seat = section[0][0];
    const { sectionDescription } = seat;
    const sectionHeadingId = `tn-syos-section-list-heading-id-${seat.sectionId}`;

    const headingLevel = this.rootHeadingLevel + 2;

    const heading = srOnlyElement('text', sectionDescription, { id: sectionHeadingId, role: 'heading', 'aria-level': headingLevel.toString() });

    return `
    <g aria-label="${sectionDescription}" role="listitem">
      ${heading}
      <g role="list" aria-label="${sectionDescription}">
        ${section.map((row) => this.makeRowListItem(row, sectionHeadingId)).join('')}
      </g>
    </g>`;
  }

  private makeSeatElement(seat: SyosSeat, describedBy: string) {
    const { unitSize } = this;
    const ariaDisabledAttr = seat.getIsAvailable() ? '' : 'aria-disabled="true"';

    return `
    <g role="listitem">
      <g role="button"
        ${ariaDisabledAttr}
        tabindex="-1"
        data-tn-seat-id="${seat.id}"
        class="tn-syos-seat-map__seat"
        aria-describedby="${describedBy}">
        <use
          x="${seat.x}"
          y="${seat.y}"
          width="${unitSize}"
          height="${unitSize}"
          xlink:href="#tn-syos-seat-map-icon-${seat.stateIcon.id}" />
      </g>
    </g>`;
  }

  private makeNonSeatsHtml() {
    return `
    <div class="tn-syos-seat-map__non-seats" aria-hidden="true">
      ${this.nonSeats.map(makeNonSeatHtml).join('')}
    </div>`;
  }

  private makeStageIndicatorHtml() {
    const { stageOrientation } = this;

    if (stageOrientation === StageOrientation.NONE) {
      return '';
    }

    return `
    <div class="tn-syos-seat-map__stage-indicator tn-syos-seat-map__stage-indicator--${stageOrientation}" aria-hidden="true">
      <div class="tn-syos-seat-map__stage-indicator-label">STAGE</div>
    </div>`;
  }
}

export default SyosSeatMap;
